import React, { useMemo, useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  emailBuilderCloseBlockSettings,
  emailBuilderSetActivePanel,
} from 'redux/actions/emailBuilder/emailBuilder';
import { emailBuilderShouldCloseBlockSettingsForToolbarPanel } from '../shell/emailBuilderCloseBlockSettingsForToolbarPanel';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { emailBuilderLayoutColumnDividerOffsetCss } from '../editing/emailBuilderLayoutColumnResize';
import { DropSlot } from './DropSlot';
import { GroupedRowLane } from './GroupedRowLane';
import { EditorButtonRow } from './EditorButtonRow';
import { EditableImage } from './EditableImage';
import { EditableSocial } from './EditableSocial';
import { EditableText } from './EditableText';
import { EditableButton } from './EditableButton';
import { EditableEyebrow } from './EditableEyebrow';
import { SelectableFrame } from './SelectableFrame';
import {
  emailBuilderColumnSelectionSuppressedEdges,
  isPointerInColumnGap,
  resolveColumnContainerEdgeAtSeam,
  resolveColumnEdgeStripHighlightForColumn,
  resolveColumnFrameFromHoverEvent,
  resolveColumnHoverDeferReason,
  resolveRowEdgeStripAtPointer,
  resolveSelectionEdgePointerTarget,
  resolveRowFrameFromHoverEvent,
  resolveRowHoverDeferReason,
  shouldPromoteColumnDividerRowHover,
} from './emailBuilderSelectionEdgeStrips';
import { useCanvasFrameChrome } from './canvasFrameChromeContext';
import { useActiveDropData } from './dnd/drop-slot-registry';
import type { RowChrome } from '../render/RowRenderer';
import {
  isColumnDropTarget,
  isGroupDropTarget,
} from '../domain/dnd/drop-target-highlight';
import { resolveRowLayoutWidth } from '../domain/block-width';
import { resolveDocumentColumnOverflowBlockIds } from '../domain/column-content-min';
import { editorBlockBoxStyle } from '../blocks/registry';
import { hasDescendantHover, resolveEffectiveHover } from '../domain/selection/ancestors';
import { applyBlockSelectionClick } from '../domain/selection/block-selection-gesture';
import { selectionEquals } from '../domain/selection/equals';
import { applyRowSelectionClick } from '../domain/selection/row-selection-gesture';
import {
  isContainerSelectionOverTarget,
  isDirectBlockSelection,
  isDirectButtonRowBlockTarget,
  isSameBlockTarget,
  resolveButtonRowSlotIndex,
  shouldDeferChildPointerActivation,
} from '../domain/selection/targets';
import { findGroup, locateRow } from '../domain/find';
import {
  translateBlockLabel,
  translateButtonRowSlotLabel,
  translateColumnNumberLabel,
  translateGroupLabel,
  translateRowLabel,
} from '../domain/labels';
import {
  isDarkColor,
  padStyle,
  resolveEditorRowSurfaceColor,
} from '../domain/presentation';
import type {
  Block,
  Column,
  Group,
  Padding,
  Row,
  Selection,
} from '../domain/types';

export type SelectionEdgeActivateEvent = Pick<
  PointerEvent,
  'button' | 'clientX' | 'clientY' | 'metaKey' | 'ctrlKey' | 'shiftKey'
>;

export type EditorCanvasChrome = {
  rowChrome: RowChrome;
  handleSelectionEdgeActivate: (event: SelectionEdgeActivateEvent) => boolean;
};

export function useCanvasChrome(): EditorCanvasChrome {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editingMode = useAppSelector(store => store.emailBuilderStore.editingMode);
  const {
    document,
    selection,
    selectedBlockIds,
    primaryBlockId,
    anchorBlockId,
    selectedRowIds,
    primaryRowId,
    anchorRowId,
    hover,
    previewHover,
    labelStackAnchor,
    columnSeamEdgeHighlight,
    rowEdgeHighlight,
    textEditingBlock,
    setSelection,
    setBlockSelection,
    setRowSelection,
    setHover,
    setColumnSeamEdgeHighlight,
    setRowEdgeHighlight,
    setPreviewHover,
    closeLabelStack,
  } = useCanvasFrameChrome();
  const activeDropData = useActiveDropData();
  const inlineContentEditing = textEditingBlock !== null;
  const effectiveHover = resolveEffectiveHover(document.sections, selection, hover);
  const chromeHover = inlineContentEditing
    ? previewHover ??
      (hover !== null &&
      (hover.kind === 'block' || hover.kind === 'buttonRowSlot')
        ? null
        : hover)
    : previewHover ?? effectiveHover;
  const labelOwner =
    textEditingBlock !== null
      ? {
          kind: 'block' as const,
          rowId: textEditingBlock.rowId,
          columnId: textEditingBlock.columnId,
          blockId: textEditingBlock.blockId,
        }
      : labelStackAnchor ??
        (effectiveHover !== null &&
        selection.kind !== 'email' &&
        isContainerSelectionOverTarget(
          selection,
          effectiveHover,
          document.sections,
        )
          ? selection
          : effectiveHover ??
            (selection.kind === 'email' ? null : selection));

  const handleBlockSelectionClick = useCallback(
    (args: {
      blockId: string;
      metaKey: boolean;
      ctrlKey: boolean;
      shiftKey: boolean;
    }) => {
      const next = applyBlockSelectionClick({
        document,
        current: {
          selectedBlockIds,
          primaryBlockId,
          anchorBlockId,
        },
        ...args,
      });
      setBlockSelection(next);
    },
    [
      anchorBlockId,
      document,
      primaryBlockId,
      selectedBlockIds,
      setBlockSelection,
    ],
  );

  const handleRowSelectionClick = useCallback(
    (args: {
      rowId: string;
      metaKey: boolean;
      ctrlKey: boolean;
      shiftKey: boolean;
    }) => {
      const next = applyRowSelectionClick({
        document,
        current: {
          selectedRowIds,
          primaryRowId,
          anchorRowId,
        },
        rowId: args.rowId,
        metaKey: args.metaKey,
        ctrlKey: args.ctrlKey,
        shiftKey: args.shiftKey,
      });
      setRowSelection(next);
    },
    [
      anchorRowId,
      document,
      primaryRowId,
      selectedRowIds,
      setRowSelection,
    ],
  );

  const openAddElementsForColumn = useCallback(
    (next: Extract<Selection, { kind: 'column' }>) => {
      if (
        emailBuilderShouldCloseBlockSettingsForToolbarPanel(
          'add-elements',
          editingMode,
        )
      ) {
        dispatch(emailBuilderCloseBlockSettings());
      }
      setColumnSeamEdgeHighlight(null);
      setSelection(next);
      setHover(next);
      dispatch(emailBuilderSetActivePanel('add-elements'));
    },
    [
      dispatch,
      editingMode,
      setColumnSeamEdgeHighlight,
      setHover,
      setSelection,
    ],
  );

  return useMemo(() => {
    const groupAncestor = (group: Group) => {
      const next: Selection = { kind: 'group', groupId: group.id };
      return {
        label: translateGroupLabel(t),
        level: 'group' as const,
        onSelect: (event: MouseEvent) => {
          event.stopPropagation();
          setSelection(next);
          setHover(next);
          closeLabelStack();
        },
        onHover: () => setPreviewHover(next),
      };
    };

    const rowAncestor = (row: Row) => {
      const next: Selection = { kind: 'row', rowId: row.id };
      return {
        label: translateRowLabel(t),
        level: 'row' as const,
        onSelect: (event: MouseEvent) => {
          event.stopPropagation();
          setSelection(next);
          setHover(next);
          closeLabelStack();
        },
        onHover: () => setPreviewHover(next),
      };
    };

    const columnAncestor = (row: Row, column: Column) => {
      const next: Selection = {
        kind: 'column',
        rowId: row.id,
        columnId: column.id,
      };
      return {
        label: translateColumnNumberLabel(t, document, row.id, column.id),
        level: 'column' as const,
        onSelect: (event: MouseEvent) => {
          event.stopPropagation();
          setSelection(next);
          setHover(next);
          closeLabelStack();
        },
        onHover: () => setPreviewHover(next),
      };
    };

    const sectionIndexForGroup = (groupId: string) =>
      document.sections.findIndex(section => section.id === groupId);

    const columnOverflowByRow = resolveDocumentColumnOverflowBlockIds(document);

    const isRowSelected = (rowId: string): boolean =>
      selectedRowIds.includes(rowId) ||
      (selection.kind === 'row' && selection.rowId === rowId);

    const isRowLayoutMode = (rowId: string): boolean =>
      isRowSelected(rowId) ||
      (selection.kind === 'column' && selection.rowId === rowId);

    const isRowChromeActive = (rowId: string): boolean =>
      isRowSelected(rowId) ||
      (chromeHover?.kind === 'row' && chromeHover.rowId === rowId);

    const applyRowFrameHover = (rowId: string, event: MouseEvent) => {
      event.stopPropagation();
      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
        setHover({ kind: 'row', rowId });
        return;
      }
      const rowFrame = resolveRowFrameFromHoverEvent(event);
      if (rowFrame !== null) {
        const edge = resolveRowEdgeStripAtPointer({
          clientX: event.clientX,
          clientY: event.clientY,
          rowFrame,
        });
        if (edge !== null) {
          setColumnSeamEdgeHighlight(null);
          setHover({ kind: 'row', rowId });
          return;
        }
      }
      if (
        isPointerInColumnGap({
          clientX: event.clientX,
          clientY: event.clientY,
          rowFrame,
        }) &&
        shouldPromoteColumnDividerRowHover({
          clientX: event.clientX,
          clientY: event.clientY,
          rowFrame,
        })
      ) {
        setColumnSeamEdgeHighlight(null);
        setHover({ kind: 'row', rowId });
        return;
      }
      const deferReason = resolveRowHoverDeferReason({
        clientX: event.clientX,
        clientY: event.clientY,
        rowFrame,
      });
      if (
        deferReason === 'column-content' &&
        isPointerInColumnGap({
          clientX: event.clientX,
          clientY: event.clientY,
          rowFrame,
        })
      ) {
        const seamColumn = resolveColumnContainerEdgeAtSeam({
          clientX: event.clientX,
          clientY: event.clientY,
          rowFrame,
        });
        if (seamColumn !== null) {
          setHover({
            kind: 'column',
            rowId,
            columnId: seamColumn.columnId,
          });
          setColumnSeamEdgeHighlight({
            rowId,
            columnId: seamColumn.columnId,
            edge: seamColumn.edge,
          });
          return;
        }
        setColumnSeamEdgeHighlight(null);
        setHover({ kind: 'row', rowId });
        return;
      }
      if (deferReason !== null) {
        return;
      }
      setColumnSeamEdgeHighlight(null);
      setHover({ kind: 'row', rowId });
    };

    const handleSelectionEdgeActivate = (
      event: SelectionEdgeActivateEvent,
    ): boolean => {
      if (event.button !== 0) {
        return false;
      }
      const target = resolveSelectionEdgePointerTarget({
        clientX: event.clientX,
        clientY: event.clientY,
        selection,
        textEditingBlock,
      });
      if (target === null) {
        return false;
      }

      if ('columnId' in target) {
        setColumnSeamEdgeHighlight(target);
        setRowEdgeHighlight(null);
        setSelection({
          kind: 'column',
          rowId: target.rowId,
          columnId: target.columnId,
        });
        setHover({
          kind: 'column',
          rowId: target.rowId,
          columnId: target.columnId,
        });
        return true;
      }

      handleRowSelectionClick({
        rowId: target.rowId,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
      });
      setColumnSeamEdgeHighlight(null);
      setRowEdgeHighlight(target);
      setHover({ kind: 'row', rowId: target.rowId });
      closeLabelStack();
      return true;
    };

    const rowChrome: RowChrome = {
      wrapGroup: (group: Group, node) => {
        const current: Selection = { kind: 'group', groupId: group.id };
        const index = sectionIndexForGroup(group.id);
        const isLast = index === document.sections.length - 1;
        const frame = (
          <SelectableFrame
            level="group"
            entityId={`group:${group.id}`}
            label={translateGroupLabel(t)}
            empty={group.rows.length === 0}
            selected={selectionEquals(selection, current)}
            descendantHovered={hasDescendantHover(
              current,
              chromeHover,
              document.sections,
            )}
            hovered={
              chromeHover ? selectionEquals(chromeHover, current) : false
            }
            showLabel={
              labelOwner ? selectionEquals(labelOwner, current) : false
            }
            dropTarget={isGroupDropTarget(activeDropData, group.id)}
            drag={{
              id: `item-group:${group.id}`,
              data: { kind: 'item-group', groupId: group.id },
            }}
            onSelect={(event: MouseEvent) => {
              event.stopPropagation();
              setSelection(current);
            }}
            onHover={(event: MouseEvent) => {
              event.stopPropagation();
              setHover(current);
            }}
            hoverTarget={{
              kind: 'group',
              groupId: group.id,
              sectionIndex: index,
            }}>
            {node}
          </SelectableFrame>
        );

        return (
          <>
            <DropSlot
              id={`section-slot:${index}`}
              data={{ kind: 'section-slot', index }}
              variant="section"
            />
            {frame}
            {isLast ? (
              <DropSlot
                id={`section-slot:${index + 1}`}
                data={{ kind: 'section-slot', index: index + 1 }}
                variant="section"
              />
            ) : null}
          </>
        );
      },
      wrapEmptyGroupRows: (group: Group) => (
        <div className={"email-builder-canvas-frame__group-row-stack"} data-empty="true">
          <DropSlot
            id={`group-row-slot:${group.id}:0:fill`}
            data={{ kind: 'group-row-slot', groupId: group.id, index: 0 }}
            variant="group-row-fill"
            empty
          />
        </div>
      ),
      wrapGroupedRow: (group: Group, row: Row, rowIndex: number, node) => {
        const current: Selection = { kind: 'row', rowId: row.id };
        const isLastInGroup = rowIndex === group.rows.length - 1;
        const rowSelected = isRowSelected(row.id);
        const frame = (
          <SelectableFrame
            level="row"
            entityId={`row:${row.id}`}
            label={translateRowLabel(t)}
            insideGroup
            groupRowIndex={rowIndex}
            groupRowLast={isLastInGroup}
            selectionEdgeForcedHighlight={
              !rowSelected &&
              rowEdgeHighlight !== null &&
              rowEdgeHighlight.rowId === row.id
                ? [rowEdgeHighlight.edge]
                : []
            }
            descendantHovered={hasDescendantHover(
              current,
              chromeHover,
              document.sections,
            )}
            ancestors={[groupAncestor(group)]}
            frameSelection={current}
            selected={rowSelected}
            hovered={
              chromeHover ? selectionEquals(chromeHover, current) : false
            }
            showLabel={
              labelOwner ? selectionEquals(labelOwner, current) : false
            }
            drag={{
              id: `item-row:${row.id}`,
              data: { kind: 'item-row', rowId: row.id },
            }}
            onSelect={(event: MouseEvent) => {
              event.stopPropagation();
              const togglingOff =
                (event.metaKey || event.ctrlKey) &&
                selectedRowIds.includes(row.id);
              handleRowSelectionClick({
                rowId: row.id,
                metaKey: event.metaKey,
                ctrlKey: event.ctrlKey,
                shiftKey: event.shiftKey,
              });
              setHover(current);
              closeLabelStack();
              if (togglingOff) {
                const active = globalThis.document.activeElement;
                if (active instanceof HTMLElement) {
                  active.blur();
                }
              }
            }}
            onHover={(event: MouseEvent) => {
              applyRowFrameHover(row.id, event);
            }}
            hoverTarget={{ kind: 'row', rowId: row.id }}>
            {node}
          </SelectableFrame>
        );

        return (
          <GroupedRowLane>
            <DropSlot
              id={`group-row-slot:${group.id}:${rowIndex}`}
              data={{
                kind: 'group-row-slot',
                groupId: group.id,
                index: rowIndex,
              }}
              variant="group-row"
            />
            {frame}
            {isLastInGroup ? (
              <DropSlot
                id={`group-row-slot:${group.id}:${rowIndex + 1}`}
                data={{
                  kind: 'group-row-slot',
                  groupId: group.id,
                  index: rowIndex + 1,
                }}
                variant="group-row"
                rowHitAnchor="below"
              />
            ) : null}
          </GroupedRowLane>
        );
      },
      wrapRow: (row: Row, node) => {
        const current: Selection = { kind: 'row', rowId: row.id };
        const located = locateRow(document.sections, row.id);
        const index =
          located?.location.parent === 'document'
            ? located.location.sectionIndex
            : -1;
        const isLast = index === document.sections.length - 1;
        const inRowSelection = selectedRowIds.includes(row.id);
        const rowSelectedProp =
          inRowSelection || selectionEquals(selection, current);
        const rowHoveredProp = chromeHover
          ? selectionEquals(chromeHover, current)
          : false;
        const rowShowLabel = labelOwner
          ? selectionEquals(labelOwner, current)
          : false;
        const frame = (
          <SelectableFrame
            level="row"
            entityId={`row:${row.id}`}
            label={translateRowLabel(t)}
            selectionEdgeForcedHighlight={
              !rowSelectedProp &&
              rowEdgeHighlight !== null &&
              rowEdgeHighlight.rowId === row.id
                ? [rowEdgeHighlight.edge]
                : []
            }
            descendantHovered={hasDescendantHover(
              current,
              chromeHover,
              document.sections,
            )}
            selected={rowSelectedProp}
            hovered={rowHoveredProp}
            showLabel={rowShowLabel}
            dropTarget={false}
            drag={{
              id: `item-row:${row.id}`,
              data: { kind: 'item-row', rowId: row.id },
            }}
            onSelect={(event: MouseEvent) => {
              event.stopPropagation();
              const togglingOff =
                (event.metaKey || event.ctrlKey) &&
                selectedRowIds.includes(row.id);
              handleRowSelectionClick({
                rowId: row.id,
                metaKey: event.metaKey,
                ctrlKey: event.ctrlKey,
                shiftKey: event.shiftKey,
              });
              setHover(current);
              closeLabelStack();
              if (togglingOff) {
                const active = globalThis.document.activeElement;
                if (active instanceof HTMLElement) {
                  active.blur();
                }
              }
            }}
            onHover={(event: MouseEvent) => {
              applyRowFrameHover(row.id, event);
            }}
            hoverTarget={{ kind: 'row', rowId: row.id }}>
            {node}
          </SelectableFrame>
        );

        if (index < 0) {
          return frame;
        }

        return (
          <>
            <DropSlot
              id={`section-slot:${index}`}
              data={{ kind: 'section-slot', index }}
              variant="section"
            />
            {frame}
            {isLast ? (
              <DropSlot
                id={`section-slot:${index + 1}`}
                data={{ kind: 'section-slot', index: index + 1 }}
                variant="section"
              />
            ) : null}
          </>
        );
      },
      wrapColumn: (row: Row, column: Column, node, padding: Padding) => {
        const current: Selection = {
          kind: 'column',
          rowId: row.id,
          columnId: column.id,
        };
        const located = locateRow(document.sections, row.id);
        const groupSection =
          located?.location.parent === 'group'
            ? findGroup(document.sections, located.location.groupId)
            : undefined;
        const subtleDropSlot = Boolean(
          column.backgroundImage || row.backgroundImage,
        );
        const columnAncestors = groupSection
          ? [groupAncestor(groupSection), rowAncestor(row)]
          : [rowAncestor(row)];
        const columnIndex = row.columns.findIndex(item => item.id === column.id);
        const blockActiveInColumn =
          (selection.kind === 'block' &&
            selection.rowId === row.id &&
            selection.columnId === column.id) ||
          (textEditingBlock !== null &&
            textEditingBlock.rowId === row.id &&
            textEditingBlock.columnId === column.id);
        const selectionEdgeSuppressedEdges =
          emailBuilderColumnSelectionSuppressedEdges(blockActiveInColumn);
        const columnSelected = selectionEquals(selection, current);
        const selectionEdgeForcedHighlight =
          !columnSelected &&
          columnSeamEdgeHighlight !== null &&
          columnSeamEdgeHighlight.rowId === row.id &&
          columnSeamEdgeHighlight.columnId === column.id
            ? [columnSeamEdgeHighlight.edge]
            : [];
        return (
          <SelectableFrame
            level="column"
            entityId={`column:${row.id}:${column.id}`}
            label={translateColumnNumberLabel(t, document, row.id, column.id)}
            empty={column.blocks.length === 0}
            boxStyle={padStyle(padding)}
            selectionEdgeForcedHighlight={selectionEdgeForcedHighlight}
            selectionEdgeSuppressedEdges={selectionEdgeSuppressedEdges}
            selected={columnSelected}
            descendantHovered={hasDescendantHover(
              current,
              chromeHover,
              document.sections,
            )}
            hovered={
              chromeHover ? selectionEquals(chromeHover, current) : false
            }
            showLabel={
              labelOwner ? selectionEquals(labelOwner, current) : false
            }
            dropTarget={isColumnDropTarget(
              activeDropData,
              row.id,
              column.id,
            )}
            ancestors={columnAncestors}
            frameSelection={current}
            onSelect={(event: MouseEvent) => {
              event.stopPropagation();
              setColumnSeamEdgeHighlight(null);
              setSelection(current);
              setHover(current);
            }}
            onHover={(event: MouseEvent) => {
              event.stopPropagation();
              if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
                setHover(current);
                return;
              }
              const columnFrame = resolveColumnFrameFromHoverEvent(event);
              const deferReason = resolveColumnHoverDeferReason({
                rowId: row.id,
                columnId: column.id,
                columnIndex,
                columnCount: row.columns.length,
                selection,
                textEditingBlock,
                clientX: event.clientX,
                clientY: event.clientY,
                columnFrame,
              });
              if (deferReason !== null) {
                if (
                  deferReason === 'block-active-edge' ||
                  deferReason === 'block-content'
                ) {
                  setColumnSeamEdgeHighlight(null);
                }
                return;
              }
              if (columnSelected) {
                setColumnSeamEdgeHighlight(null);
              }
              setHover(current);
            }}
            hoverTarget={{
              kind: 'column',
              rowId: row.id,
              columnId: column.id,
            }}>
            <div
              className={"email-builder-canvas-frame__column-stack"}
              data-empty={column.blocks.length === 0 ? 'true' : 'false'}
              data-valign={column.verticalAlign}>
              {node}
              {column.blocks.length > 0 ? (
                <DropSlot
                  id={`block-slot:${row.id}:${column.id}:${column.blocks.length}`}
                  data={{
                    kind: 'block-slot',
                    rowId: row.id,
                    columnId: column.id,
                    index: column.blocks.length,
                    zone: 'insert',
                    edge: 'end',
                  }}
                />
              ) : null}
              {column.blocks.length === 0 ? (
                <DropSlot
                  id={`block-slot:${row.id}:${column.id}:0:fill`}
                  data={{
                    kind: 'block-slot',
                    rowId: row.id,
                    columnId: column.id,
                    index: 0,
                    zone: 'fill',
                  }}
                  variant="fill"
                  empty
                  subtle={subtleDropSlot}
                  onEmptyClick={() => {
                    openAddElementsForColumn(current);
                  }}
                />
              ) : null}
            </div>
          </SelectableFrame>
        );
      },
      wrapColumnRow: (row: Row, node) => {
        if (row.columns.length < 2) {
          return node;
        }
        const innerWidth =
          resolveRowLayoutWidth(document, row.id)?.innerWidth ?? 0;
        const rowSelection: Selection = { kind: 'row', rowId: row.id };
        const located = locateRow(document.sections, row.id);
        const groupSection =
          located?.location.parent === 'group'
            ? findGroup(document.sections, located.location.groupId)
            : undefined;
        const surfaceColor = resolveEditorRowSurfaceColor(
          row.backgroundColor,
          groupSection?.backgroundColor,
          document.settings.contentBackgroundColor,
        );
        return (
          <div className={'email-builder-column-resize-overlay'}>
            {node}
            {row.columns.slice(0, -1).map((_, leftIndex) => (
              <ColumnResizeHandle
                key={leftIndex}
                rowId={row.id}
                leftIndex={leftIndex}
                left={emailBuilderLayoutColumnDividerOffsetCss(
                  row.columnWidths,
                  leftIndex,
                  row.columnGap,
                )}
                leftPercent={row.columnWidths[leftIndex] ?? 0}
                rightPercent={row.columnWidths[leftIndex + 1] ?? 0}
                innerWidth={innerWidth}
                gap={row.columnGap}
                rowActive={isRowSelected(row.id)}
                layoutActive={isRowLayoutMode(row.id)}
                onDarkSurface={isDarkColor(surfaceColor)}
                onSelect={() => {
                  setSelection(rowSelection);
                  setHover(rowSelection);
                }}
              />
            ))}
          </div>
        );
      },
      wrapBlock: (row: Row, column: Column, block: Block, node) => {
        const current: Selection = {
          kind: 'block',
          rowId: row.id,
          columnId: column.id,
          blockId: block.id,
        };
        const index = column.blocks.findIndex(item => item.id === block.id);
        const activeSlotIndex =
          block.type === 'buttonRow'
            ? resolveButtonRowSlotIndex(
                effectiveHover,
                selection,
                row.id,
                column.id,
                block.id,
              )
            : null;
        const inBlockSelection = selectedBlockIds.includes(block.id);
        const multiSelectActive = selectedBlockIds.length > 1;
        const frameSelected =
          inBlockSelection ||
          isSameBlockTarget(selection, row.id, column.id, block.id);
        const frameDirectSelected =
          (primaryBlockId !== null && primaryBlockId === block.id) ||
          isDirectBlockSelection(selection, row.id, column.id, block.id);
        const frameHovered =
          block.type === 'buttonRow'
            ? isDirectButtonRowBlockTarget(
                effectiveHover,
                row.id,
                column.id,
                block.id,
              )
            : chromeHover !== null &&
              isSameBlockTarget(chromeHover, row.id, column.id, block.id);
        const frameLabel =
          block.type === 'buttonRow' && activeSlotIndex !== null
            ? translateButtonRowSlotLabel(t, activeSlotIndex)
            : translateBlockLabel(t, block);
        const located = locateRow(document.sections, row.id);
        const groupSection =
          located?.location.parent === 'group'
            ? findGroup(document.sections, located.location.groupId)
            : undefined;
        const buttonRowAncestor =
          block.type === 'buttonRow' && activeSlotIndex !== null
            ? [
                {
                  label: translateBlockLabel(t, block),
                  level: 'block' as const,
                  onSelect: (event: MouseEvent) => {
                    event.stopPropagation();
                    setSelection(current);
                    setHover(current);
                    closeLabelStack();
                  },
                  onHover: () => setPreviewHover(current),
                },
              ]
            : [];
        const blockAncestors = groupSection
          ? [
              groupAncestor(groupSection),
              rowAncestor(row),
              columnAncestor(row, column),
              ...buttonRowAncestor,
            ]
          : [rowAncestor(row), columnAncestor(row, column), ...buttonRowAncestor];
        const inner =
          block.type === 'text' ? (
            <EditableText
              key={block.id}
              rowId={row.id}
              columnId={column.id}
              block={block}
            />
          ) : block.type === 'button' ? (
            <EditableButton
              key={block.id}
              rowId={row.id}
              columnId={column.id}
              block={block}
            />
          ) : block.type === 'eyebrow' ? (
            <EditableEyebrow
              key={block.id}
              rowId={row.id}
              columnId={column.id}
              block={block}
            />
          ) : block.type === 'buttonRow' ? (
            <EditorButtonRow
              key={block.id}
              rowId={row.id}
              columnId={column.id}
              block={block}
              hover={effectiveHover}
              setHover={setHover}
            />
          ) : block.type === 'image' ? (
            <EditableImage
              key={block.id}
              rowId={row.id}
              columnId={column.id}
              block={block}
            />
          ) : block.type === 'social' ? (
            <EditableSocial key={block.id} block={block} />
          ) : (
            node
          );
        const isFillColumnImage =
          block.type === 'image' && block.heightMode === 'fillColumn';
        const overflowingBlocks = columnOverflowByRow.get(row.id);
        const layoutOverflow =
          overflowingBlocks?.has(block.id) === true &&
          isRowChromeActive(row.id);
        const isInlineEditing =
          textEditingBlock !== null &&
          textEditingBlock.rowId === row.id &&
          textEditingBlock.columnId === column.id &&
          textEditingBlock.blockId === block.id &&
          (block.type === 'text' ||
            block.type === 'button' ||
            block.type === 'eyebrow' ||
            (block.type === 'buttonRow' &&
              textEditingBlock.slotIndex !== undefined));
        const frame = (
          <SelectableFrame
            level="block"
            entityId={`block:${block.id}`}
            label={frameLabel}
            boxStyle={editorBlockBoxStyle(block, isFillColumnImage)}
            layoutOverflow={layoutOverflow}
            selected={frameSelected}
            subselected={
              !multiSelectActive && frameSelected && !frameDirectSelected
            }
            hovered={frameHovered}
            descendantHovered={hasDescendantHover(
              current,
              chromeHover,
              document.sections,
            )}
            textEditing={isInlineEditing}
            textVariant={
              block.type === 'text'
                ? block.variant
                : block.type === 'button' ||
                    block.type === 'eyebrow' ||
                    block.type === 'buttonRow'
                  ? 'button'
                  : undefined
            }
            inlineTextBlockId={
              block.type === 'text' ||
              block.type === 'button' ||
              block.type === 'eyebrow' ||
              block.type === 'buttonRow'
                ? block.id
                : undefined
            }
            showLabel={
              labelOwner !== null &&
              isSameBlockTarget(labelOwner, row.id, column.id, block.id)
            }
            drag={
              isInlineEditing
                ? undefined
                : {
                    id: `item-block:${block.id}`,
                    data: {
                      kind: 'item-block',
                      rowId: row.id,
                      columnId: column.id,
                      blockId: block.id,
                    },
                  }
            }
            ancestors={blockAncestors}
            frameSelection={current}
            onSelect={(event: MouseEvent) => {
              event.stopPropagation();
              if (
                shouldDeferChildPointerActivation(
                  selection,
                  current,
                  document.sections,
                  event,
                )
              ) {
                return false;
              }
              handleBlockSelectionClick({
                blockId: block.id,
                metaKey: event.metaKey,
                ctrlKey: event.ctrlKey,
                shiftKey: event.shiftKey,
              });
              setHover(current);
              closeLabelStack();
            }}
            onHover={(event: MouseEvent) => {
              event.stopPropagation();
              if (
                Number.isFinite(event.clientX) &&
                Number.isFinite(event.clientY)
              ) {
                const columnFrame = resolveColumnFrameFromHoverEvent(event);
                const rowFrame =
                  columnFrame instanceof HTMLElement
                    ? columnFrame.closest(
                        '.email-builder-canvas-frame[data-level="row"]',
                      )
                    : null;
                const rowEdge =
                  rowFrame instanceof HTMLElement
                    ? resolveRowEdgeStripAtPointer({
                        clientX: event.clientX,
                        clientY: event.clientY,
                        rowFrame,
                      })
                    : null;
                if (rowEdge !== null) {
                  setColumnSeamEdgeHighlight(null);
                  setHover({ kind: 'row', rowId: row.id });
                  return;
                }
                if (!frameSelected) {
                  const edge = resolveColumnEdgeStripHighlightForColumn({
                    clientX: event.clientX,
                    clientY: event.clientY,
                    columnId: column.id,
                    columnFrame,
                    rowFrame: rowFrame instanceof HTMLElement ? rowFrame : null,
                  });
                  if (edge !== null) {
                    setHover({
                      kind: 'column',
                      rowId: row.id,
                      columnId: column.id,
                    });
                    return;
                  }
                }
              }
              setColumnSeamEdgeHighlight(null);
              setHover(current);
            }}
            hoverTarget={{
              kind: 'block',
              rowId: row.id,
              columnId: column.id,
              blockId: block.id,
              index,
            }}>
            {inner}
          </SelectableFrame>
        );
        return (
          <>
            <DropSlot
              id={`block-slot:${row.id}:${column.id}:${index}`}
              data={{
                kind: 'block-slot',
                rowId: row.id,
                columnId: column.id,
                index,
                zone: 'insert',
                edge: index === 0 ? 'start' : 'between',
              }}
            />
            {frame}
          </>
        );
      },
    };

    return { rowChrome, handleSelectionEdgeActivate };
  }, [
    activeDropData,
    columnSeamEdgeHighlight,
    rowEdgeHighlight,
    document,
    hover,
    chromeHover,
    effectiveHover,
    labelOwner,
    previewHover,
    selection,
    selectedBlockIds,
    primaryBlockId,
    anchorBlockId,
    selectedRowIds,
    handleBlockSelectionClick,
    handleRowSelectionClick,
    openAddElementsForColumn,
    closeLabelStack,
    setColumnSeamEdgeHighlight,
    setRowEdgeHighlight,
    setHover,
    setPreviewHover,
    setSelection,
    textEditingBlock,
    t,
  ]);
}
